import { useEffect, useState } from "react";
import { getInfluxdbData } from "../../../services/api/influxdb-tools";
import { Panel } from "../../../components/panels/Panel";
import { Bar } from "react-chartjs-2";
import {
  getLISIQuery,
  getLISITRSQuery,
  LISI_CONFIGURATIONS, LISI_OPERATIONS
} from "../../../services/dashboard/lisi-dashboard-service";
import { InputSelect } from "../../../components/forms/inputs/InputSelect";
import { MiniPanel } from "../../../components/panels/MiniPanel";
import { formatSecondsToHMS } from "../../../helpers/date/date-helper";
import { Loader } from "../../../components/loading/Loader";


export const LisiDashboardGlobalData = ({ selectedRange }) => {

  const [filterOperation, setFilterOperation] = useState(LISI_OPERATIONS[0]);
  const [filterConfiguration, setFilterConfiguration] = useState(LISI_CONFIGURATIONS[0]);
  const [trsData, setTrsData] = useState([]);
  const [globalKPI, setGlobalKPI] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!selectedRange) return;
    const start = Math.floor(selectedRange.start / 1000);
    const end = Math.floor(selectedRange.end / 1000);

    const getData = async () => {
      let data;
      setIsLoading(true);
      data = await getInfluxdbData(
        null, null, [], [], null, null, null, null, null, null, null,
        getLISIQuery('global_kpi', start, end),
      );
      const result = data.reduce((acc, {
        data_pattern_operation,
        data_pattern_execution_configuration,
        ...metrics
      }) => {
        acc[data_pattern_operation || 'all'] ??= {}
        acc[data_pattern_operation || 'all'][data_pattern_execution_configuration || 'all'] = metrics

        return acc
      }, {})
      setIsLoading(false);
      setGlobalKPI(result);
    };

    getData();
  }, [selectedRange]);

  const labels = (trsData?.[filterOperation.value]?.[filterConfiguration.value] ?? []).map(({ _time }, index) => {
    if (!_time) return index + 1;
    return new Date(_time).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
    });
  });

  /* TRS graph */
  useEffect(() => {
    if (!selectedRange) return;
    const getData = async () => {
      let data = await getInfluxdbData(
        'engine',
        null,
        [],
        [],
        null,
        null,
        null,
        null,
        null,
        [],
        null,
        getLISITRSQuery(Math.floor(selectedRange.start / 1000), Math.floor(selectedRange.end / 1000)),
        false,
        null,
      );
      const result = data.reduce((acc, item) => {
        const operationKey = item.data_pattern_operation || 'all'
        const configurationKey = item.data_pattern_execution_configuration || 'all'

        acc[operationKey] ??= {}
        acc[operationKey][configurationKey] ??= []

        acc[operationKey][configurationKey].push({
          _time: item._time,
          _value: item._value
        })

        return acc
      }, {})
      setTrsData(result);
    };
    getData();
  }, [selectedRange]);

  const data = {
    labels,
    datasets: [
      {
        type: 'line',
        order: 1,
        label: 'TRS',
        data: (trsData?.[filterOperation.value]?.[filterConfiguration.value] ?? []).map(({ _value }) => _value),
        borderColor: '#4164FD',
        backgroundColor: 'rgba(65, 100, 253, 0.2)',
        fill: true,
        tension: 0.35,
        pointRadius: 0,
        pointHoverRadius: 5,
      }
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top'
      }
    },
    scales: {
      x: {
        stacked: false,
        grid: {
          display: false
        }
      },
      y: {
        type: 'linear',
        position: 'left',
        beginAtZero: true,
        min: 0,
        max: 1,
        title: {
          display: true,
          text: 'TRS',
        }
      },
    }
  };

  const kpiValues = globalKPI?.[filterOperation.value]?.[filterConfiguration.value];

  return (
    <Panel title="DONNEES GLOBALES DE LA PRODUCTION" rounded width="w-2/3">
      {isLoading && <Loader/>}

      <div className="flex flex-col h-full gap-4">
        <Panel noHeader rounded category="secondary" overflowAuto={false} height="fit-content">
          <div className="flex gap-4">
            Opération :
            <InputSelect
              items={LISI_OPERATIONS}
              onChange={setFilterOperation}
              itemToString={(item) => item?.label}
              defaultValue={LISI_OPERATIONS[0]}
              className="min-w-[200px]"
            />

            Configuration :
            <InputSelect
              items={LISI_CONFIGURATIONS}
              onChange={setFilterConfiguration}
              itemToString={(item) => item?.label}
              defaultValue={LISI_CONFIGURATIONS[0]}
              className="min-w-[200px]"
            />
          </div>
        </Panel>

        <div className="min-h-0 grid grid-cols-4 grid-rows-4 gap-4 flex-1">
          <div className="bg-perception-black-700 rounded-md p-4 flex flex-col items-center justify-center text-center gap-2">
            <div className="text-5xl">{kpiValues ? kpiValues?.struck_pieces_count : '-'}</div>
            <div className="text-sm">Nombre de pièces frappées et analysées par l'IA</div>
          </div>
          <div className="bg-perception-black-700 rounded-md p-4 flex flex-col items-center justify-center text-center gap-2">
            <div className="text-5xl">{kpiValues ? Math.floor(kpiValues?.conformity_ratio * 1000) / 10 : '-'}%</div>
            <div className="text-sm">Taux de pièces conformes</div>
          </div>
          <div className="bg-perception-black-700 rounded-md p-4 flex flex-col items-center justify-center text-center gap-2">
            <div className="text-5xl">{kpiValues ? Math.floor(kpiValues?.average_pieces_per_minute * 10) / 10 : '-'}</div>
            <div className="text-sm">Cadence moyenne (pièces/minute)</div>
          </div>
          <div className="bg-perception-black-700 rounded-md p-4 flex flex-col items-center justify-center text-center gap-2">
            <div className="text-5xl">{kpiValues?.pattern_matched_ratio ? Math.floor(kpiValues?.pattern_matched_ratio * 1000) / 10 + '%' : '-'}</div>
            <div className="text-sm">Taux d'utilisation de la solution IA</div>
          </div>

          <div className="bg-perception-black-700 rounded-md p-4 flex flex-col items-center justify-center text-center gap-2">
            <div className="text-5xl">{kpiValues ? formatSecondsToHMS(kpiValues?.oven_time_mean) : '-'}</div>
            <div className="text-sm">Temps de chauffe moyen</div>
          </div>
          <div className="bg-perception-black-700 rounded-md p-4 flex flex-col items-center justify-center text-center gap-2">
            <div className="text-5xl">{kpiValues ? formatSecondsToHMS(kpiValues?.transfer_time_mean) : '-'}</div>
            <div className="text-sm">Temps de transfert moyen</div>
          </div>
          <div className="bg-perception-black-700 rounded-md p-4 flex flex-col items-center justify-center text-center gap-2">
            <div className="text-5xl">{kpiValues?.exceeded_oven_time_count ? kpiValues?.exceeded_oven_time_count : 0}</div>
            <div className="text-sm">Nombre de temps de chauffe dépassés</div>
          </div>
          <div className="bg-perception-black-700 rounded-md p-4 flex flex-col items-center justify-center text-center gap-2">
            <div className="text-5xl">{kpiValues?.mean_surplus_oven_time_vs_pattern ? formatSecondsToHMS(kpiValues?.mean_surplus_oven_time_vs_pattern) : '-'}</div>
            <div className="text-sm">Temps moyen de surplus par pièce dans le four</div>
          </div>

          <Panel noHeader={true} rounded={true} category="secondary" className="col-span-3 row-span-2">
            <Bar data={data} options={options} />
          </Panel>

          <div className="bg-perception-black-700 rounded-md p-4 flex flex-col items-center justify-center text-center gap-2">
            <div className="text-5xl">{kpiValues ? kpiValues?.total_pieces_count : '-'}</div>
            <div className="text-sm">Total pièces produites (analysées ou non par l'IA)</div>
          </div>

          <div className="bg-perception-black-700 rounded-md p-4 flex flex-col items-center justify-center text-center gap-2">
            <div className="text-5xl">{kpiValues ? Math.floor(kpiValues?.production_time_ratio * 1000) / 10 : '-'}%</div>
            <div className="text-sm">TRS moyen</div>
          </div>
        </div>
      </div>
    </Panel>
  );
};